import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, LogIn } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import { FButton } from "@/components/common/FButton";
import { NAV } from "@/data/content";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-ff-midnight/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Logo />

        {/* desktop nav */}
        <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={`text-sm font-medium transition-colors ${
                  active ? "text-ff-ivory" : "text-ff-grey hover:text-ff-ivory"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* desktop actions */}
        <div className="hidden items-center gap-3 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="login-dropdown-trigger"
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-ff-grey transition-colors hover:text-ff-ivory focus:outline-none"
              >
                <LogIn className="h-4 w-4" /> Login
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 border-white/10 bg-ff-slate text-ff-ivory"
            >
              <DropdownMenuLabel className="text-ff-grey">Portals</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-ff-ivory">
                <Link to="/login" data-testid="login-creator-item">Creator Login</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-ff-ivory">
                <Link to="/login" data-testid="login-ambassador-item">Ambassador Login</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-ff-ivory">
                <Link to="/login" data-testid="login-admin-item">Admin Login</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <FButton to="/apply" data-testid="header-apply-btn" className="px-6 py-2.5">
            Apply <ArrowRight className="h-4 w-4" />
          </FButton>
        </div>

        {/* mobile */}
        <Sheet>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-trigger"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ff-ivory lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[86%] border-white/10 bg-ff-midnight p-0 text-ff-ivory sm:w-96"
          >
            <div className="flex h-16 items-center justify-between border-b border-white/10 px-5">
              <Logo />
              <SheetClose asChild>
                <button aria-label="Close menu" className="text-ff-grey hover:text-ff-ivory">
                  <X className="h-6 w-6" />
                </button>
              </SheetClose>
            </div>
            <nav className="flex flex-col p-5" data-testid="mobile-nav">
              {NAV.map((item) => (
                <SheetClose asChild key={item.to}>
                  <Link
                    to={item.to}
                    className="border-b border-white/5 py-4 font-display text-lg font-medium text-ff-ivory"
                  >
                    {item.label}
                  </Link>
                </SheetClose>
              ))}
            </nav>
            <div className="flex flex-col gap-3 px-5">
              <SheetClose asChild>
                <FButton to="/apply" data-testid="mobile-apply-btn">
                  Apply to FanFuel <ArrowRight className="h-4 w-4" />
                </FButton>
              </SheetClose>
              <SheetClose asChild>
                <FButton variant="secondary" to="/login" data-testid="mobile-login-btn">
                  Login
                </FButton>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;
